<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { $api } from '@/services';
import { _ROUTER_NAME } from '@/enums';
import Loading from '@/components/common/Loading.vue';
import ScreeningFilter from './features/ScreeningFilter.vue';
import ScreeningCreate from './features/ScreeningCreate.vue';
import ScreeningTitleModal from './features/ScreeningTitleModal.vue';
import ScreeningSendModal from './features/ScreeningSendModal.vue';

const store = useStore();
const router = useRouter();

const scales = [
	{ id: 1, name: 'Thang C.A.R.S', short: 'C.A.R.S' },
	{ id: 2, name: 'Thang A.S.Q 3', short: 'A.S.Q 3' },
	{ id: 3, name: 'Thang D.S.M 5', short: 'D.S.M 5' },
];

const columns = [
	{ key: 'id', label: 'STT', sort: 'id' },
	{ key: 'student', label: 'Học sinh', sort: 'student_name' },
	{ key: 'title', label: 'Tiêu đề', sort: 'title' },
	{ key: 'state', label: 'Trạng thái', sort: 'state' },
	{ key: 'date', label: 'Ngày thực hiện', sort: 'date' },
	{ key: 'age', label: 'Tuổi thực' },
	{ key: 'total_score', label: 'Tổng điểm', sort: 'total_score' },
	{ key: 'conclusion', label: 'Kết luận' },
	{ key: 'files', label: 'Tài liệu' },
];

const isLoading = ref(true);
const items = ref([]);
const scaleId = ref(1);
const studentId = ref('');
const stateFilter = ref('');
const orderBy = ref('');
const filterOpen = ref(false);
const createOpen = ref(false);
const titleOpen = ref(false);
const creating = ref(false);
const titleError = ref('');
const createContext = ref(null);
const sendOpen = ref(false);
const sending = ref(false);
const sendTarget = ref(null);

const activeScale = computed(
	() => scales.find((item) => item.id === scaleId.value) || scales[0]
);

const isEmpty = computed(() => !isLoading.value && items.value.length === 0);

const statusKind = (item) => {
	if (item.state === 'draft') return 'draft';
	if (item.state === 'sent') return 'sent';
	if (item.state === 'unsend') return 'unsend';
	if (item.sent_at) return 'sent';
	return 'unsend';
};

const fetchList = async () => {
	isLoading.value = true;
	try {
		const params = {
			screening_scale_id: scaleId.value,
		};
		if (studentId.value) params.student_id = studentId.value;
		if (stateFilter.value) params.state = stateFilter.value;
		if (orderBy.value) params.order_by = orderBy.value;
		const res = await $api.screening.list(params);
		const body = res?.data;
		items.value = Array.isArray(body) ? body : body?.data || [];
	} catch (err) {
		items.value = [];
		await store.dispatch('setAlertMessage', {
			message:
				err?.response?.data?.errors?.join('<br/>') ||
				'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	} finally {
		isLoading.value = false;
	}
};

const selectScale = (id) => {
	if (scaleId.value === id) return;
	scaleId.value = id;
	fetchList();
};

const toggleSort = (column) => {
	if (!column) return;
	const asc = `${column}_asc`;
	const desc = `${column}_desc`;
	orderBy.value = orderBy.value === asc ? desc : asc;
	fetchList();
};

const sortMark = (column) => {
	if (!column) return '';
	if (orderBy.value === `${column}_asc`) return 'asc';
	if (orderBy.value === `${column}_desc`) return 'desc';
	return '';
};

const scaleShort = (name) => {
	if (!name) return '';
	return String(name)
		.split(' - ')[0]
		.replace(/^thang\s+/i, '');
};

const defaultTitle = computed(() => {
	const scale = createContext.value?.scale;
	const student = createContext.value?.student;
	return `Thang sàng lọc ${scaleShort(scale?.name)} - ${
		student?.name || ''
	}`.trim();
});

const errorText = (err) => {
	const errors = err?.response?.data?.errors;
	const text = Array.isArray(errors)
		? errors.join(' ')
		: errors && typeof errors === 'object'
		? Object.values(errors).flat().join(' ')
		: err?.response?.data?.error || err?.response?.data?.message || '';
	if (/trùng|duplicate|taken|tiêu đề|title/i.test(text)) {
		return 'Tiêu đề không được trùng với tiêu đề đã có';
	}
	return text || 'Đã có lỗi xảy ra. Vui lòng thử lại';
};

const onContinue = (payload) => {
	createContext.value = payload;
	titleError.value = '';
	titleOpen.value = true;
};

const confirmCreate = async (title) => {
	const context = createContext.value;
	if (!context || creating.value) return;
	creating.value = true;
	titleError.value = '';
	try {
		const res = await $api.screening.create(context.student.id, {
			student_id: context.student.id,
			screening_scale_id: context.scale.id,
			title,
		});
		const body = res?.data?.data || res?.data || {};
		const screeningId = body.id || body.student_screening_scale?.id;
		titleOpen.value = false;
		createOpen.value = false;
		router.push({
			name: _ROUTER_NAME.SCREENING_TAKE,
			params: {
				studentId: context.student.id,
				screeningId,
			},
		});
	} catch (err) {
		titleError.value = errorText(err);
	} finally {
		creating.value = false;
	}
};

const openRow = (row) => {
	if (!row.student?.id || !row.id) return;
	router.push({
		name: _ROUTER_NAME.SCREENING_TAKE,
		params: {
			studentId: row.student.id,
			screeningId: row.id,
		},
		query: statusKind(row) === 'draft' ? {} : { view: 'detail' },
	});
};

const applyFilter = (payload) => {
	studentId.value = payload.student_id || '';
	stateFilter.value = payload.state || '';
	filterOpen.value = false;
	fetchList();
};

const formatDate = (value) => {
	if (!value) return '-';
	const [year, month, day] = String(value).slice(0, 10).split('-');
	if (!year || !month || !day) return '-';
	return `${day}/${month}/${year}`;
};

const display = (value) => {
	if (value === null || value === undefined || value === '') return '-';
	return value;
};

const fileUrl = (file) => {
	if (!file) return '';
	if (typeof file === 'string') return file;
	return file.url || file.file_url || file.link || file.path || '';
};

const hasFiles = (item) => Array.isArray(item.files) && item.files.length > 0;

const downloadFiles = (item) => {
	(item.files || []).forEach((file) => {
		const url = fileUrl(file);
		if (url) window.open(url, '_blank');
	});
};

const openSend = (row) => {
	if (!row?.student?.id || !row?.id || sending.value) return;
	sendTarget.value = row;
	sendOpen.value = true;
};

const closeSend = () => {
	if (sending.value) return;
	sendOpen.value = false;
	sendTarget.value = null;
};

const confirmSend = async () => {
	const row = sendTarget.value;
	if (!row?.student?.id || !row?.id || sending.value) return;
	sending.value = true;
	try {
		await $api.screening.send(row.student.id, row.id);
		row.state = 'sent';
		row.sent_at = row.sent_at || new Date().toISOString();
		sendOpen.value = false;
		sendTarget.value = null;
		store.dispatch('setAlertMessage', {
			message: 'Gửi tới phụ huynh thành công.',
			type: 'success',
		});
	} catch (err) {
		const errors = err?.response?.data?.errors;
		await store.dispatch('setAlertMessage', {
			message: Array.isArray(errors)
				? errors.join('<br/>')
				: 'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	} finally {
		sending.value = false;
	}
};

onMounted(fetchList);
</script>

<template>
	<div class="screening px-12 md-down:px-4 pt-12 pb-16">
		<div class="flex flex-wrap items-center justify-between gap-4">
			<div class="flex items-center gap-3">
				<img
					src="@/assets/images/icons/clipboard-list.png"
					class="w-7 h-7"
					alt=""
				/>
				<h1 class="screening__title">Sàng lọc</h1>
			</div>
			<div class="flex items-center gap-6">
				<button
					type="button"
					class="button-p btn--ghost w-fit px-5"
					@click="createOpen = true"
				>
					Tạo mới
				</button>
				<button
					type="button"
					class="screening__filter"
					@click="filterOpen = true"
				>
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
						<path
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M1.5 3.2c0-.4.3-.7.7-.7h11.6c.4 0 .7.3.7.7s-.3.7-.7.7H2.2c-.4 0-.7-.3-.7-.7Zm1.6 4.3c0-.4.3-.7.7-.7h8.4c.4 0 .7.3.7.7s-.3.7-.7.7H3.8c-.4 0-.7-.3-.7-.7Zm1.8 3.6c-.4 0-.7.3-.7.7s.3.7.7.7h4.8c.4 0 .7-.3.7-.7s-.3-.7-.7-.7H4.9Z"
							fill="#48566A"
						/>
					</svg>
					Bộ lọc
				</button>
			</div>
		</div>

		<div class="screening__tabs mt-12">
			<button
				v-for="scale in scales"
				:key="scale.id"
				type="button"
				class="screening__tab"
				:class="{ 'screening__tab--active': scaleId === scale.id }"
				@click="selectScale(scale.id)"
			>
				{{ scale.name }}
			</button>
		</div>

		<div v-if="isLoading" class="flex justify-center py-24">
			<Loading />
		</div>

		<div v-else-if="isEmpty" class="flex flex-col items-center pt-12">
			<img
				src="@/assets/images/illustrations/screening-empty.svg"
				alt=""
				class="screening__empty-art"
			/>
			<div class="screening__empty-copy">
				<p>
					Trung tâm chưa có kết quả sàng lọc nào sử dụng thang
					{{ activeScale.short }}.
				</p>
				<p class="mt-6">
					Bản đánh giá này cho phép sử dụng các thang sàng lọc có sẵn
					như C.A.R.S, A.S.Q 3, D.S.M 5 để hỗ trợ sàng lọc và xác định
					mức độ biểu hiện của trẻ.
				</p>
			</div>
		</div>

		<div v-else class="mt-8">
			<section>
				<div class="overflow-x-auto">
					<table class="screening-table">
						<thead>
							<tr>
								<th
									v-for="column in columns"
									:key="column.key"
									:class="{ 'cursor-pointer': column.sort }"
									@click="toggleSort(column.sort)"
								>
									<span
										class="inline-flex items-center gap-1"
									>
										{{ column.label }}
										<svg
											v-if="
												sortMark(column.sort) === 'asc'
											"
											width="12"
											height="12"
											viewBox="0 0 12 12"
											fill="none"
										>
											<path
												d="M2 8l4-4 4 4"
												stroke="#48566A"
												stroke-width="1.4"
												stroke-linecap="round"
											/>
										</svg>
										<svg
											v-else-if="
												sortMark(column.sort) === 'desc'
											"
											width="12"
											height="12"
											viewBox="0 0 12 12"
											fill="none"
										>
											<path
												d="M2 4l4 4 4-4"
												stroke="#48566A"
												stroke-width="1.4"
												stroke-linecap="round"
											/>
										</svg>
									</span>
								</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="(row, index) in items"
								:key="row.id"
								:class="{ 'cursor-pointer': row.student?.id }"
								@click="openRow(row)"
							>
								<td>{{ index + 1 }}</td>
								<td>
									<div
										class="flex items-center gap-3 whitespace-nowrap"
									>
										<img
											v-if="
												row.student?.profile_photo?.w200
											"
											:src="
												row.student.profile_photo.w200
											"
											alt=""
											class="h-6 w-6 rounded-full object-cover"
										/>
										<span
											v-else
											class="inline-flex items-center justify-center h-6 w-6 rounded-full text-white text-xs font-medium"
											:style="{
												background:
													row.student?.profile_photo
														?.default?.color ||
													'#F1A267',
											}"
										>
											{{
												row.student?.profile_photo
													?.default?.initial
											}}
										</span>
										<span
											class="font-semibold"
											:style="{
												color:
													row.student?.profile_photo
														?.default?.color ||
													'#F1A267',
											}"
										>
											{{ row.student?.name || '-' }}
										</span>
									</div>
								</td>
								<td>{{ display(row.title) }}</td>
								<td>
									<button
										v-if="statusKind(row) === 'unsend'"
										type="button"
										class="screening__send"
										@click.stop="openSend(row)"
									>
										Gửi PH
									</button>
									<span v-else-if="statusKind(row) === 'sent'"
										>Đã gửi</span
									>
									<span v-else>Bản nháp</span>
								</td>
								<td class="whitespace-nowrap">
									{{
										statusKind(row) === 'draft' && !row.date
											? '-'
											: formatDate(row.date)
									}}
								</td>
								<td class="whitespace-nowrap">
									{{ display(row.display_age) }}
								</td>
								<td>{{ display(row.total_score) }}</td>
								<td>{{ display(row.conclusion) }}</td>
								<td>
									<button
										v-if="hasFiles(row)"
										type="button"
										class="p-2 text-blue-600"
										@click.stop="downloadFiles(row)"
									>
										<svg
											width="20"
											height="20"
											viewBox="0 0 20 20"
											fill="none"
										>
											<path
												d="M10 3.5v8.2m0 0l-3-3m3 3l3-3M4.5 14.5v1A1.5 1.5 0 0 0 6 17h8a1.5 1.5 0 0 0 1.5-1.5v-1"
												stroke="currentColor"
												stroke-width="1.6"
												stroke-linecap="round"
												stroke-linejoin="round"
											/>
										</svg>
									</button>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</section>
		</div>

		<ScreeningFilter
			v-if="filterOpen"
			:student-id="studentId"
			:state="stateFilter"
			@close="filterOpen = false"
			@apply="applyFilter"
		/>
		<ScreeningCreate
			v-if="createOpen"
			@close="createOpen = false"
			@continue="onContinue"
		/>
		<ScreeningSendModal
			v-if="sendOpen"
			:loading="sending"
			@close="closeSend"
			@confirm="confirmSend"
		/>
		<ScreeningTitleModal
			v-if="titleOpen"
			:scale-name="createContext?.scale?.name || ''"
			:default-title="defaultTitle"
			:error="titleError"
			:loading="creating"
			@close="titleOpen = false"
			@confirm="confirmCreate"
		/>
	</div>
</template>

<style lang="scss" scoped>
.screening__title {
	font-size: 28px;
	line-height: 32px;
	letter-spacing: -0.56px;
	font-weight: 700;
	color: #65758b;
}

.screening__filter {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 12px 20px;
	border-radius: 4px;
	background: #fff;
	color: #48566a;
	font-size: 14px;
	font-weight: 600;
	box-shadow: 0 0 1px rgba(12, 26, 75, 0.24), 0 3px 8px rgba(50, 50, 71, 0.05);
}

.screening__tabs {
	display: flex;
	gap: 32px;
	border-bottom: 1px solid #e1e7ef;
}

.screening__tab {
	padding: 0 4px 16px;
	font-size: 14px;
	line-height: 20px;
	color: #6b7280;
	border-bottom: 2px solid transparent;
	margin-bottom: -1px;
}

.screening__tab--active {
	color: #d89210;
	font-weight: 600;
	border-bottom-color: #f0bb4f;
}

.screening__empty-art {
	width: 250px;
	height: 250px;
	object-fit: contain;
	transform: rotate(180deg) scaleX(-1);
}

.screening__empty-copy {
	width: 540px;
	max-width: 100%;
	margin-top: 24px;
	text-align: center;
	font-size: 18px;
	line-height: 24px;
	color: #94a3b8;
}

.screening-table {
	width: 100%;
	min-width: 1100px;
	border-collapse: separate;
	border-spacing: 0;
	background: #fff;
	border-radius: 8px;
	overflow: hidden;
	box-shadow: 0 0 1px rgba(12, 26, 75, 0.24), 0 3px 8px rgba(50, 50, 71, 0.05);

	th {
		background: #f1f5f9;
		padding: 24px 16px;
		text-align: left;
		font-size: 12px;
		line-height: 16px;
		font-weight: 600;
		letter-spacing: 0.96px;
		text-transform: uppercase;
		color: #48566a;
		white-space: nowrap;
	}

	td {
		height: 72px;
		padding: 16px;
		font-size: 16px;
		line-height: 24px;
		color: #344256;
		background: #fff;
		border-top: 1px solid #edf2f7;
		vertical-align: middle;
	}
}

.screening__send {
	padding: 8px 12px;
	border-radius: 4px;
	border: 1px solid #d89210;
	color: #d89210;
	font-size: 12px;
	line-height: 16px;
	font-weight: 600;
	background: #fff;
}
</style>
